import React, { useState, useEffect, useCallback, useRef } from 'react';
import { User, InterviewSession, Transcription, AIResponse } from '../types';
import { useLiveKit } from '../hooks/useLiveKit';
import { useWebSocket } from '../hooks/useWebSocket';
import { PythonBackendService } from '../services/pythonBackendService';
import { APIEndpoints } from '../config/APIEndpoints';
import {
  getInterviewCopilotEntitlement,
  startInterviewCopilotBillingSession,
  stopInterviewCopilotBillingSession,
  formatRemainingTime,
  InterviewCopilotEntitlement,
} from '../services/saasBillingService';

import { PythonAudioControls, SessionMode } from './interview/PythonAudioControls';
import { TranscriptionPanel } from './interview/TranscriptionPanel';
import { AIProviderPanel } from './interview/AIProviderPanel';
import { SessionStatus } from './interview/SessionStatus';
import { QuestionBank } from './interview/QuestionBank';

import { LogOut, MessageSquare, BookOpen, Bot, Menu, X, ArrowLeftRight, Mic, MicOff, Clipboard, Send, Moon, Sun } from 'lucide-react';

interface PythonInterviewDashboardProps {
  user: User;
  onLeaveSession: () => void;
  onSessionIdChange?: (sessionId: string) => void;
}

export const PythonInterviewDashboard: React.FC<PythonInterviewDashboardProps> = ({
  user,
  onLeaveSession,
  onSessionIdChange,
}) => {
  const THEME_KEY = 'interview-copilot-theme';
  const [currentSession, setCurrentSession] = useState<InterviewSession | null>(null);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [showQuestionBank, setShowQuestionBank] = useState(false);
  const [showTranscriptions, setShowTranscriptions] = useState(false);
  const [showLeaveSessionConfirm, setShowLeaveSessionConfirm] = useState(false);
  const [aiResponses, setAIResponses] = useState<AIResponse[]>([]);
  const [transcriptions, setTranscriptions] = useState<Transcription[]>([]);

  // Debug: Log transcriptions state changes
  useEffect(() => {
    console.log('📊 Transcriptions state updated. Count:', transcriptions.length);
    console.log('📊 Transcriptions:', transcriptions);
  }, [transcriptions]);
  const [sessionMode, setSessionMode] = useState<SessionMode>('meet-voice-chat');
  const [showOpenAI, setShowOpenAI] = useState<boolean>(true);
  const [showGemini, setShowGemini] = useState<boolean>(true);
  const [showMobileMenu, setShowMobileMenu] = useState<boolean>(false);
  const [panelsSwapped, setPanelsSwapped] = useState<boolean>(false);
  const [currentRoom, setCurrentRoom] = useState<string | null>(null);  // **FIX FOR USER ISOLATION**
  const [manualTriggerEnabled, setManualTriggerEnabled] = useState<boolean>(false);
  const [showMicToggle, setShowMicToggle] = useState<boolean>(false);  // Show mic toggle for audio modes
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [billingEntitlement, setBillingEntitlement] = useState<InterviewCopilotEntitlement | null>(null);
  const billingTrackedRef = useRef<boolean>(false);
  /** Last billing session id we started — used so stop always hits the API even if refs glitch */
  const activeBillingSessionIdRef = useRef<string | null>(null);
  const [showHoursExhaustedModal, setShowHoursExhaustedModal] = useState(false);
  const [creditsExhaustedCountdown, setCreditsExhaustedCountdown] = useState<number | null>(null);
  const hoursCutoffTriggeredRef = useRef(false);
  const creditsShutdownExecutedRef = useRef(false);
  const stopInterviewSessionRef = useRef<(() => Promise<void>) | null>(null);

  /** Poll while session is active (external users) to detect plan hours exhaustion */
  const MEETING_HOURS_POLL_MS = 15000;
  const CREDITS_SHUTDOWN_COUNTDOWN_SEC = 10;

  // Store current transcript (single transcription, not accumulated)
  const [currentTranscript, setCurrentTranscript] = useState<string>('');
  
  // Use ref to track manual trigger state to avoid recreating callbacks
  const manualTriggerRef = useRef<boolean>(false);
  
  // Use ref to track if we're currently sending (to prevent race conditions)
  const isSendingRef = useRef<boolean>(false);
  
  // Sync ref with state
  useEffect(() => {
    manualTriggerRef.current = manualTriggerEnabled;
  }, [manualTriggerEnabled]);

  useEffect(() => {
    const savedTheme = localStorage.getItem(THEME_KEY);
    if (savedTheme === 'dark') {
      setIsDarkMode(true);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(THEME_KEY, isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ent = await getInterviewCopilotEntitlement();
        if (!cancelled) setBillingEntitlement(ent);
      } catch (e) {
        console.warn('Could not load plan entitlement:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // LiveKit hook
  const {
    isConnected: isLiveKitConnected,
    connect: connectLiveKit,
    disconnect: disconnectLiveKit,
    toggleMicrophone,
    isMicrophoneMuted,
    resetMicrophone
  } = useLiveKit();

  // WebSocket hook for live transcriptions
  const handleNewTranscription = useCallback((transcription: Transcription) => {
    console.log('📝 New transcription received:', transcription);

    const isChatbotSend = transcription.source === 'chatbot';
    const manualOn = manualTriggerRef.current;

    // Panel: messages sent to the LLM — chatbot Send always; live audio when manual trigger is OFF (auto mode).
    const shouldShowInPanel =
      isChatbotSend || (!manualOn && Boolean(transcription.text));

    if (shouldShowInPanel) {
      setTranscriptions(prev => {
        const isDuplicate = prev.some(t => t.id === transcription.id);
        if (isDuplicate) {
          console.log(`⚠️ Duplicate LLM message in panel (ID: ${transcription.id}), ignoring`);
          return prev;
        }
        console.log('✅ Adding sent-to-LLM message to transcriptions panel');
        return [transcription, ...prev];
      });
    }

    // Live speech feeds the input bar only when manual trigger is ON (not shown in panel until user sends).
    if (manualOn && transcription.text && !isChatbotSend) {
      // Skip if we're in the middle of sending to prevent race conditions
      if (isSendingRef.current) {
        console.log('⏳ Skip appending - send in progress');
        return;
      }
      
      setCurrentTranscript(prev => {
        const newText = prev ? prev + ' ' + transcription.text : transcription.text;
        console.log('🔘 Manual trigger: appending transcript');
        console.log('   Previous:', prev || '(empty)');
        console.log('   New:', transcription.text);
        console.log('   Combined:', newText);
        return newText;
      });
    }
  }, []);

  // Handle AI responses from WebSocket
  // Handle AI responses from WebSocket
const handleAIResponse = useCallback((aiResponseData: any) => {
  console.log('🤖 New AI response received:', aiResponseData);
  
  // Convert backend AI response to frontend AIResponse type
  const aiResponse: AIResponse = {
    id: aiResponseData.id,
    transcriptionId: aiResponseData.transcriptionId,
    timestamp: new Date(aiResponseData.timestamp),
    question: aiResponseData.question,
    responses: {
      openai: {
        text: aiResponseData.response.text,
        confidence: aiResponseData.response.confidence,
        model: aiResponseData.response.model
      },
      gemini: null
    },
    selectedResponse: 'openai', // Auto-select OpenAI response
    userFeedback: undefined,
    streamingProvider: undefined // Add this
  };
  
  setAIResponses(prev => {
    // Check if this AI response ID already exists in our state
    const isDuplicate = prev.some(r => r.id === aiResponse.id);
    
    if (isDuplicate) {
      console.log(`⚠️ Duplicate AI response detected (ID: ${aiResponse.id}), ignoring`);
      return prev;
    }
    
    return [...prev, aiResponse];
  });
}, []);

// Handle streaming AI response start
const handleAIResponseStart = useCallback((data: any) => {
  console.log('🚀 AI response started:', data);
  
  const provider = (data.provider || 'openai') as 'openai' | 'gemini'; // Type assertion
  
  // Create a placeholder AI response that will be updated with streaming content
  const streamingResponse: AIResponse = {
    id: data.id,
    transcriptionId: data.transcriptionId,
    timestamp: new Date(data.timestamp),
    question: data.question,
    responses: {
      openai: provider === 'openai' ? {
        text: '', // Start with empty text
        confidence: 0,
        model: 'gpt-4o'
      } : null,
      gemini: provider === 'gemini' ? {
        text: '',
        confidence: 0,
        model: 'gemini-2.5-pro'
      } : null
    },
    selectedResponse: provider,
    userFeedback: undefined,
    isStreaming: true,
    streamingProvider: provider // Track which provider is streaming
  };
  
  setAIResponses(prev => {
    // Find if we already have a response with this transcriptionId
    const existingIndex = prev.findIndex(r => r.transcriptionId === data.transcriptionId);
    
    if (existingIndex >= 0) {
      // Update existing response to add new provider
      const updated = [...prev];
      updated[existingIndex] = {
        ...updated[existingIndex],
        responses: {
          ...updated[existingIndex].responses,
          [provider]: {
            text: '',
            confidence: 0,
            model: provider === 'openai' ? 'gpt-4o' : 'gemini-2.5-pro'
          }
        }
      };
      return updated;
    }
    
    return [...prev, streamingResponse];
  });
}, []);

// Handle streaming AI response chunks
const handleAIResponseChunk = useCallback((data: any) => {
  console.log('📝 AI response chunk:', data);
  
  const provider = (data.provider || 'openai') as 'openai' | 'gemini'; // Type assertion
  
  setAIResponses(prev => prev.map(response => {
    if (response.transcriptionId === data.transcriptionId) {
      const currentProviderResponse = response.responses[provider];
      
      return {
        ...response,
        responses: {
          ...response.responses,
          [provider]: {
            text: (currentProviderResponse?.text || '') + data.content,
            confidence: currentProviderResponse?.confidence || 0,
            model: currentProviderResponse?.model || (provider === 'openai' ? 'gpt-4o' : 'gemini-2.5-pro')
          }
        }
      };
    }
    return response;
  }));
}, []);

// Handle streaming AI response completion
const handleAIResponseComplete = useCallback((data: any) => {
  console.log('✅ AI response completed:', data);
  
  const provider = (data.provider || 'openai') as 'openai' | 'gemini'; // Type assertion
  
  setAIResponses(prev => prev.map(response => {
    if (response.transcriptionId === data.transcriptionId) {
      return {
        ...response,
        responses: {
          ...response.responses,
          [provider]: {
            text: data.fullResponse,
            confidence: data.confidence,
            model: data.model
          }
        },
        isStreaming: response.streamingProvider === provider ? false : response.isStreaming
      };
    }
    return response;
  }));
}, []);

// Handle streaming AI response errors
const handleAIResponseError = useCallback((data: any) => {
  console.log('❌ AI response error:', data);
  
  const provider = (data.provider || 'openai') as 'openai' | 'gemini'; // Type assertion
  
  setAIResponses(prev => prev.map(response => {
    if (response.transcriptionId === data.transcriptionId && response.isStreaming) {
      return {
        ...response,
        responses: {
          ...response.responses,
          [provider]: {
            text: 'Sorry, there was an error generating the response.',
            confidence: 0,
            model: provider === 'openai' ? 'gpt-4o' : 'gemini-2.5-pro'
          }
        },
        isStreaming: false
      };
    }
    return response;
  }));
}, []); const {
    connect: connectWebSocket,
    disconnect: disconnectWebSocket
  } = useWebSocket(
    handleNewTranscription, 
    handleAIResponse,
    handleAIResponseStart,
    handleAIResponseChunk,
    handleAIResponseComplete,
    handleAIResponseError,
    disconnectLiveKit // Pass LiveKit disconnect function to handle WebSocket disconnections
  );

  // Python Backend Service
  const [backendService] = useState(() => new PythonBackendService());

  const stopBillingTimer = useCallback(async (sessionId: string | undefined) => {
    const idToStop = sessionId || activeBillingSessionIdRef.current;
    // Always attempt stop when we have an id — backend is idempotent for already-completed rows.
    // Do not gate only on billingTrackedRef (that can leave a DB row "active" and keep charging).
    if (!idToStop) {
      billingTrackedRef.current = false;
      activeBillingSessionIdRef.current = null;
      return;
    }
    try {
      await stopInterviewCopilotBillingSession(idToStop);
      console.log('✅ Billing session timer stopped:', idToStop);
      const ent = await getInterviewCopilotEntitlement();
      setBillingEntitlement(ent);
    } catch (error) {
      console.error('⚠️ Failed to stop billing session timer:', error);
    } finally {
      billingTrackedRef.current = false;
      activeBillingSessionIdRef.current = null;
    }
  }, []);

  const startInterviewSession = async (mode: SessionMode) => {
    // Always mint a new session id — reusing a stopped/completed one returns 409
    const sessionId = `session_${Date.now()}`;
    console.log(`🆔 Starting with new session ID: ${sessionId}`);

    try {
      console.log(`Starting interview session in ${mode} mode...`);

      try {
        const entitlement = await getInterviewCopilotEntitlement();
        setBillingEntitlement(entitlement);
        if (!entitlement.isInternal && !entitlement.canUse) {
          alert(entitlement.message || 'You cannot start a session with your current plan.');
          return;
        }
      } catch (entError) {
        console.error('Entitlement check failed:', entError);
        alert('Unable to verify your plan. Please try again or contact support.');
        return;
      }

      // Bind the new id on the Python backend so transcriptions attach correctly
      try {
        await backendService.updateSessionId(user.id!, sessionId);
        onSessionIdChange?.(sessionId);
      } catch (sessionBindError) {
        console.error('Failed to bind new session id:', sessionBindError);
        alert('Unable to prepare a new session. Please try again.');
        return;
      }

      try {
        await startInterviewCopilotBillingSession(sessionId);
        billingTrackedRef.current = true;
        activeBillingSessionIdRef.current = sessionId;
        console.log('✅ Billing session timer started:', sessionId);
      } catch (billingError: unknown) {
        const err = billingError as Error & { message?: string };
        console.error('Billing session start blocked:', billingError);
        billingTrackedRef.current = false;
        activeBillingSessionIdRef.current = null;
        alert(err.message || 'Cannot start session with your current plan.');
        return;
      }

      // Keep prior Q&A on screen — backend LLM history also persists until Leave.
      // Only clear the draft input so leftover typed text isn't resent.
      setCurrentTranscript('');
      setSharedChatInput('');
      
      // Store the session mode
      setSessionMode(mode);
      
      const userId = user.id || 'anonymous';
      
      console.log(`🔍 PythonInterviewDashboard: Using user ID: ${userId}`);
      console.log(`🔍 Session Mode: ${mode}`);
      console.log(`🔍 User object:`, user);
      
      // User preferences are already set during onboarding
      console.log('✅ User preferences already configured during onboarding');
      
      // Only connect LiveKit and audio capture if not in chat-only mode
      let roomName: string | null = null;
      if (mode === 'chat-only') {
        console.log('💬 Chat-only mode: Skipping LiveKit and audio capture');
        // **FIX FOR USER ISOLATION**: Use userId as room name in chat-only mode
        // This ensures each user gets their own isolated responses
        roomName = `chat_${userId}`;
        console.log('🏠 Using chat-isolated room:', roomName);
      } else if (mode === 'meet-voice-chat') {
        // Connect to LiveKit room with display audio (tab audio)
        // Room name will be unique for each user to prevent joining each other's rooms
        roomName = await connectLiveKit(userId, undefined, 'display');
        setCurrentRoom(roomName);
        setShowMicToggle(true);  // Enable mic toggle for tab audio
        resetMicrophone();  // Start with mic enabled
        console.log('✅ Connected to LiveKit room (display audio mode):', roomName);
      } else if (mode === 'mic-voice-chat') {
        // Connect to LiveKit room with microphone audio
        // Room name will be unique for each user to prevent joining each other's rooms
        roomName = await connectLiveKit(userId, undefined, 'microphone');
        setCurrentRoom(roomName);
        setShowMicToggle(true);
        resetMicrophone();  // Start with mic enabled
        console.log('✅ Connected to LiveKit room (microphone audio mode):', roomName);
      }
      
      // Connect to WebSocket for live transcriptions (needed for all modes for chatbot)
      // **FIX FOR USER ISOLATION**: Pass room name for filtering
      await connectWebSocket(undefined, roomName || undefined);
      console.log('✅ Connected to WebSocket for live transcriptions', roomName ? `(room: ${roomName})` : '');
      
      // Create session
      const session: InterviewSession = {
        id: sessionId,
        userId: user.id!,
        startTime: new Date(),
        platform: 'google-meet',
        transcriptions: [],
        aiResponses: [],
        status: 'active'
      };

      setCurrentSession(session);
      setIsSessionActive(true);
      hoursCutoffTriggeredRef.current = false;
      creditsShutdownExecutedRef.current = false;
      setShowHoursExhaustedModal(false);
      setCreditsExhaustedCountdown(null);
      
      console.log('✅ Interview session started successfully!');
      
      if (mode === 'meet-voice-chat') {
        console.log('🎧 Now capturing tab audio - please select a tab with audio playing');
      } else if (mode === 'mic-voice-chat') {
        console.log('🎤 Now capturing microphone audio - speak to get AI responses');
      } else if (mode === 'chat-only') {
        console.log('💬 Chat-only mode active - use the chatbot input to ask questions');
      }
      
    } catch (error) {
      console.error('❌ Failed to start interview session:', error);
      await stopBillingTimer(sessionId);

      // Show mode-specific error message
      let errorDetails = '';
      if (mode === 'meet-voice-chat') {
        errorDetails = '\n\nPlease check:\n• Browser permissions for screen capture\n• Python backend server is running\n• You\'re using Chrome or Edge browser';
      } else if (mode === 'mic-voice-chat') {
        errorDetails = '\n\nPlease check:\n• Browser permissions for microphone access\n• Python backend server is running\n• Your microphone is working properly';
      } else if (mode === 'chat-only') {
        errorDetails = '\n\nPlease check:\n• Python backend server is running\n• WebSocket connection is available';
      }
      
      alert(`Failed to start interview session:\n\n${(error as Error).message}${errorDetails}`);
      
      // Cleanup on failure
      if (mode === 'meet-voice-chat' || mode === 'mic-voice-chat') {
        disconnectLiveKit();
      }
      setIsSessionActive(false);
      setCurrentSession(null);
    }
  };

  const stopInterviewSession = async () => {
    const sessionId = currentSession?.id || activeBillingSessionIdRef.current || undefined;

    // Freeze billing first — duration stops accruing once the DB row is completed
    setIsSessionActive(false);
    await stopBillingTimer(sessionId);

    // Only disconnect LiveKit if we're in a mode that uses it
    if (sessionMode === 'meet-voice-chat' || sessionMode === 'mic-voice-chat') {
      disconnectLiveKit();
    }
    
    disconnectWebSocket();
    
    // Notify backend to stop bot for this room immediately
    // (Don't wait for webhook which may not trigger)
    if (currentRoom && sessionId) {
      try {
        console.log(`📤 Notifying backend to stop bot for room: ${currentRoom}`);
        const response = await fetch(`${APIEndpoints.BASE_URL}/session/${sessionId}/close?room_name=${currentRoom}`, {
          method: 'POST'
        });
        if (response.ok) {
          console.log(`✅ Backend notified to stop bot for room: ${currentRoom}`);
        } else {
          console.warn(`⚠️ Failed to notify backend about room cleanup: ${currentRoom}`);
        }
      } catch (error) {
        console.error('❌ Error notifying backend to stop bot:', error);
      }
    }
    
    setCurrentRoom(null);  // **FIX FOR USER ISOLATION**: Clear room on disconnect
    setShowMicToggle(false);  // Reset mic toggle visibility
    
    if (sessionId) {
      try {
        await backendService.closeSession(sessionId);
        console.log(`✅ Session closed on backend: ${sessionId}`);
      } catch (error) {
        console.error('❌ Failed to close session on backend:', error);
        // Don't block the UI, just log the error
      }
    }
    
    setCurrentSession(null);
    console.log(`Interview session stopped (mode: ${sessionMode}) — billing is not running`);
  };

  stopInterviewSessionRef.current = stopInterviewSession;

  const isMeetingHoursExhausted = useCallback((ent: InterviewCopilotEntitlement) => {
    if (ent.isInternal) return false;
    if (ent.unlimited) return false;
    if (!ent.canUse) return true;
    return ent.remainingSeconds != null && ent.remainingSeconds <= 0;
  }, []);

  const beginCreditsExhaustedShutdown = useCallback(() => {
    if (hoursCutoffTriggeredRef.current) return;
    hoursCutoffTriggeredRef.current = true;
    setShowHoursExhaustedModal(true);
    setCreditsExhaustedCountdown(CREDITS_SHUTDOWN_COUNTDOWN_SEC);
    console.warn('⏱️ Meeting hours exhausted — stopping session after countdown');
  }, [CREDITS_SHUTDOWN_COUNTDOWN_SEC]);

  /** Count down then auto-stop the interview session */
  useEffect(() => {
    if (!showHoursExhaustedModal || creditsExhaustedCountdown === null) return;

    if (creditsExhaustedCountdown <= 0) {
      if (!creditsShutdownExecutedRef.current) {
        creditsShutdownExecutedRef.current = true;
        void stopInterviewSessionRef.current?.();
      }
      return;
    }

    const timerId = window.setTimeout(() => {
      setCreditsExhaustedCountdown((prev) => (prev !== null && prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => window.clearTimeout(timerId);
  }, [showHoursExhaustedModal, creditsExhaustedCountdown]);

  /** Periodic check while interview is running */
  useEffect(() => {
    if (!isSessionActive) return;
    if (hoursCutoffTriggeredRef.current) return;

    const pollMeetingHours = async () => {
      if (hoursCutoffTriggeredRef.current || !isSessionActive) return;

      try {
        const ent = await getInterviewCopilotEntitlement();
        setBillingEntitlement(ent);
        if (ent.isInternal) return;
        if (!isMeetingHoursExhausted(ent)) return;

        beginCreditsExhaustedShutdown();
      } catch (err) {
        console.warn('Meeting hours poll failed:', err);
      }
    };

    pollMeetingHours();
    const intervalId = window.setInterval(pollMeetingHours, MEETING_HOURS_POLL_MS);
    return () => window.clearInterval(intervalId);
  }, [isSessionActive, isMeetingHoursExhausted, beginCreditsExhaustedShutdown]);

  const clearTranscriptions = () => {
    setTranscriptions([]);
  };

  const handleChatbotQuery = useCallback(async (
    question: string,
    provider: 'openai' | 'gemini' | 'both' = 'both'
  ) => {
    try {
      console.log(`💬 Sending chatbot query to ${provider}:`, question);
      
      // Single request so both providers share one transcription_id / DB row
      const result = await backendService.sendChatbotQuery(
        user.id,
        question,
        provider
      );
      
      console.log('✅ Chatbot query sent successfully:', result);
      
      // Responses will arrive via WebSocket automatically
      // handleAIResponseStart, handleAIResponseChunk, and handleAIResponseComplete
      // will handle the streaming responses
      
    } catch (error: any) {
      console.error('❌ Chatbot query failed:', error);
      
      // Show user-friendly error message
      if (error.response?.status === 404) {
        alert('Session expired. Please refresh the page and complete onboarding again.');
      } else {
        alert('Failed to send question. Please try again.');
      }
    }
  }, [user.id, backendService]);

  // Shared chat input state
  const [sharedChatInput, setSharedChatInput] = useState<string>('');
  const [isSendingShared, setIsSendingShared] = useState<boolean>(false);
  const [isPastingShared, setIsPastingShared] = useState<boolean>(false);
  const sharedChatInputRef = useRef<HTMLTextAreaElement | null>(null);

  // Track if we should sync transcript to input (disable during send)
  const shouldSyncTranscriptRef = useRef<boolean>(true);

  // Sync currentTranscript to sharedChatInput (only when sync is enabled)
  useEffect(() => {
    if (!shouldSyncTranscriptRef.current) {
      console.log('⏸️ Transcript sync disabled');
      return;
    }
    
    // Sync transcript to input (including empty string to clear it)
    console.log('🔄 Syncing transcript to input:', currentTranscript || '(empty)');
    setSharedChatInput(currentTranscript);
  }, [currentTranscript]);

  // Auto-resize shared chat input as content grows.
  useEffect(() => {
    const el = sharedChatInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [sharedChatInput]);

  const handleSharedPaste = async () => {
    if (!isSessionActive || isPastingShared) return;
    
    setIsPastingShared(true);
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setSharedChatInput(prev => prev + text);
        console.log('✅ Pasted text from clipboard');
      }
    } catch (err) {
      console.error('Failed to read clipboard:', err);
      alert('Unable to access clipboard. Please use Ctrl+V or Cmd+V to paste.');
    } finally {
      setIsPastingShared(false);
    }
  };

  const handleSharedSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sharedChatInput.trim() || isSendingShared) return;
    if (hoursCutoffTriggeredRef.current) return;

    if (!billingEntitlement?.isInternal) {
      try {
        const ent = await getInterviewCopilotEntitlement();
        setBillingEntitlement(ent);
        if (isMeetingHoursExhausted(ent)) {
          beginCreditsExhaustedShutdown();
          return;
        }
      } catch {
        alert('Unable to verify meeting hours. Please try again.');
        return;
      }
    }
    
    setIsSendingShared(true);
    const messageToSend = sharedChatInput.trim();
    
    console.log('📤 Sending message:', messageToSend);
    
    // CRITICAL: Mark that we're sending (prevents new transcriptions from appending)
    isSendingRef.current = true;
    
    // Clear states
    setCurrentTranscript('');
    setSharedChatInput('');
    
    // Disable sync to prevent interference
    shouldSyncTranscriptRef.current = false;
    
    console.log('🧹 States cleared, sending disabled');
    
    try {
      // One request with provider=both — avoids split DB rows from dual POSTs
      await handleChatbotQuery(messageToSend, 'both');
      console.log('✅ Message sent successfully');
    } catch (error) {
      console.error('❌ Failed to send message:', error);
      // On error, restore the message
      isSendingRef.current = false;
      shouldSyncTranscriptRef.current = true;
      setCurrentTranscript(messageToSend);
      alert('Failed to send message. Please try again.');
    } finally {
      // Re-enable everything after a delay to ensure clean state
      setTimeout(() => {
        isSendingRef.current = false;
        shouldSyncTranscriptRef.current = true;
        console.log('🔓 Ready for new transcriptions');
      }, 300);
      setIsSendingShared(false);
    }
  };

  return (
    <div className={`min-h-screen transition-colors ${isDarkMode ? 'bg-gray-950 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <header className={`${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'} shadow-sm border-b sticky top-0 z-30 transition-colors`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left side - Logo and toggles */}
            <div className="flex items-center space-x-2 sm:space-x-6">
              <div>
                <h1 className={`text-lg sm:text-xl font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                  Assistant
                </h1>
                {billingEntitlement &&
                  !billingEntitlement.isInternal &&
                  billingEntitlement.showUsage !== false && (
                  <p
                    className={`text-xs mt-0.5 ${
                      isMeetingHoursExhausted(billingEntitlement)
                        ? 'text-red-500 font-medium'
                        : (billingEntitlement.remainingSeconds ?? 0) > 0 &&
                            (billingEntitlement.remainingSeconds ?? 0) <= 300
                          ? 'text-amber-500 font-medium'
                          : isDarkMode
                            ? 'text-gray-400'
                            : 'text-gray-500'
                    }`}
                  >
                    {billingEntitlement.unlimited
                      ? 'Meeting hours: unlimited'
                      : isMeetingHoursExhausted(billingEntitlement)
                        ? 'Meeting hours: none left'
                        : `Meeting hours: ${formatRemainingTime(billingEntitlement.remainingSeconds)}`}
                  </p>
                )}
              </div>
              
              {/* AI Provider Toggles - Desktop */}
              <div className={`hidden lg:flex items-center space-x-2 border-l pl-6 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <span className={`text-sm mr-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>AI Providers:</span>
                
                {/* OpenAI Toggle */}
                <button
                  onClick={() => setShowOpenAI(!showOpenAI)}
                  className={`flex items-center space-x-2 h-8 px-3 rounded-full backdrop-blur-md border shadow-sm transition-all duration-200 ease-out active:scale-95 ${
                    isDarkMode
                      ? 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                      : 'bg-white/80 border-black/5 hover:bg-white'
                  } ${
                    showOpenAI
                      ? 'text-green-700 hover:bg-green-50'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  title={showOpenAI ? "Hide OpenAI panel" : "Show OpenAI panel"}
                >
                  <span className={`text-xs font-medium ${showOpenAI ? 'text-green-600' : 'text-gray-500'}`}>
                    OpenAI
                  </span>
                  <div className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    showOpenAI ? 'bg-green-600' : 'bg-gray-400'
                  }`}>
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white shadow-md transition-transform ${
                        showOpenAI ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </div>
                </button>
                
                {/* Gemini Toggle */}
                <button
                  onClick={() => setShowGemini(!showGemini)}
                  className={`flex items-center space-x-2 h-8 px-3 rounded-full backdrop-blur-md border shadow-sm transition-all duration-200 ease-out active:scale-95 ${
                    isDarkMode
                      ? 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                      : 'bg-white/80 border-black/5 hover:bg-white'
                  } ${
                    showGemini
                      ? 'text-blue-700 hover:bg-blue-50'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  title={showGemini ? "Hide Gemini panel" : "Show Gemini panel"}
                >
                  <span className={`text-xs font-medium ${showGemini ? 'text-blue-600' : 'text-gray-500'}`}>
                    Gemini
                  </span>
                  <div className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    showGemini ? 'bg-blue-600' : 'bg-gray-400'
                  }`}>
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white shadow-md transition-transform ${
                        showGemini ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </div>
                </button>
              </div>
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              >
                {showMobileMenu ? (
                  <X className="w-5 h-5" />
                ) : (
                  <Menu className="w-5 h-5" />
                )}
              </button>
            </div>
            
            {/* Right side - Session status and controls */}
            <div className="hidden sm:flex items-center space-x-3">
              {/* User Profile Display */}
              {user.email && (
                <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-full ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <div className="w-7 h-7 rounded-full bg-primary-600 flex items-center justify-center text-white text-sm font-semibold">
                    {user.email.charAt(0).toUpperCase()}
                  </div>
                  <span className={`text-sm font-medium max-w-[150px] truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    {user.email}
                  </span>
                </div>
              )}
              
              {/* Manual Trigger Toggle - Always show */}
              <button
                onClick={async () => {
                  setManualTriggerEnabled(!manualTriggerEnabled);
                  try {
                    await backendService.updateManualTrigger(user.id, !manualTriggerEnabled);
                    console.log(`✅ Manual trigger synced with backend: ${!manualTriggerEnabled}`);
                  } catch (error) {
                    console.error('❌ Failed to sync manual trigger with backend:', error);
                    setManualTriggerEnabled(manualTriggerEnabled);
                    alert('Failed to update manual trigger setting. Please try again.');
                  }
                }}
                className={`flex items-center space-x-2 h-9 px-4 rounded-full backdrop-blur-md border shadow-sm transition-all duration-200 ease-out active:scale-95 ${
                  isDarkMode
                    ? 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                    : 'bg-white/80 border-black/5 hover:bg-white'
                } ${
                  manualTriggerEnabled
                    ? 'rounded-full border-gray-300 text-primary-700 hover:bg-primary-150'
                    : 'rounded-full border-gray-300 text-gray-600 hover:bg-gray-150'
                }`}
                title={manualTriggerEnabled ? "Manual Trigger is ON - Click to turn OFF" : "Manual Trigger is OFF - Click to turn ON"}
              >
                <span className={`text-sm font-medium ${manualTriggerEnabled ? 'text-primary-600' : 'text-gray-500'}`}>
                  Manual Trigger
                </span>
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  manualTriggerEnabled ? 'bg-primary-600' : 'bg-gray-400'
                }`}>
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform ${
                      manualTriggerEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </div>
              </button>

              {/* Session Status */}
              <SessionStatus
                isActive={isSessionActive}
                isConnected={isLiveKitConnected}
              />
              
              {/* Audio Toggle Button - Show for both mic and tab audio modes */}
              {showMicToggle && isSessionActive && (
                <button
                  onClick={toggleMicrophone}
                  className={`p-2 rounded-lg transition-all ${
                    isMicrophoneMuted
                      ? 'bg-red-100 text-red-600 hover:bg-red-200'
                      : 'bg-green-100 text-green-600 hover:bg-green-200'
                  }`}
                  title={isMicrophoneMuted ? "Audio muted - Click to unmute" : "Audio active - Click to mute"}
                >
                  {isMicrophoneMuted ? (
                    <MicOff className="w-5 h-5" />
                  ) : (
                    <Mic className="w-5 h-5" />
                  )}
                </button>
              )}
              
              <PythonAudioControls
                isSupported={true}
                isSessionActive={isSessionActive}
                onStartSession={startInterviewSession}
                onStopSession={stopInterviewSession}
              />
            </div>
            
            {/* Mobile - Session Controls and Settings */}
            <div className="sm:hidden flex items-center space-x-2">
              {/* Manual Trigger Toggle - Always show */}
              <button
                onClick={async () => {
                  setManualTriggerEnabled(!manualTriggerEnabled);
                  try {
                    await backendService.updateManualTrigger(user.id, !manualTriggerEnabled);
                    console.log(`✅ Manual trigger synced with backend: ${!manualTriggerEnabled}`);
                  } catch (error) {
                    console.error('❌ Failed to sync manual trigger with backend:', error);
                    setManualTriggerEnabled(manualTriggerEnabled);
                    alert('Failed to update manual trigger setting. Please try again.');
                  }
                }}
                className={`flex items-center space-x-2 px-2 py-1.5 rounded-lg transition-all border ${
                  isDarkMode
                    ? 'bg-gray-800 border-gray-700'
                    : ''
                } ${
                  manualTriggerEnabled
                    ? 'border-primary-500 bg-primary-100'
                    : 'border-gray-300 bg-gray-100'
                }`}
                title={manualTriggerEnabled ? "Manual Trigger is ON - Click to turn OFF" : "Manual Trigger is OFF - Click to turn ON"}
              >
                <span className={`text-xs font-semibold ${manualTriggerEnabled ? 'text-primary-600' : 'text-gray-500'}`}>
                  MT
                </span>
                <div className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  manualTriggerEnabled ? 'bg-primary-600' : 'bg-gray-400'
                }`}>
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white shadow-md transition-transform ${
                      manualTriggerEnabled ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </div>
              </button>
              
              {/* Audio Toggle Button - Show for both mic and tab audio modes */}
              {showMicToggle && isSessionActive && (
                <button
                  onClick={toggleMicrophone}
                  className={`p-2 rounded-lg transition-all ${
                    isMicrophoneMuted
                      ? 'bg-red-100 text-red-600 hover:bg-red-200'
                      : 'bg-green-100 text-green-600 hover:bg-green-200'
                  }`}
                  title={isMicrophoneMuted ? "Audio muted - Click to unmute" : "Audio active - Click to mute"}
                >
                  {isMicrophoneMuted ? (
                    <MicOff className="w-4 h-4" />
                  ) : (
                    <Mic className="w-4 h-4" />
                  )}
                </button>
              )}
              <PythonAudioControls
                isSupported={true}
                isSessionActive={isSessionActive}
                onStartSession={startInterviewSession}
                onStopSession={stopInterviewSession}
              />
            </div>
          </div>
          
          {/* Mobile Menu Dropdown */}
          {showMobileMenu && (
            <div className={`lg:hidden py-4 border-t space-y-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              {/* User Profile Display - Mobile */}
              {user.email && (
                <div className={`flex items-center space-x-3 px-3 py-2 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold">
                    {user.email.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    {user.fullName && (
                      <p className={`text-sm font-semibold truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{user.fullName}</p>
                    )}
                    <p className={`text-xs truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{user.email}</p>
                  </div>
                </div>
              )}
              
              {/* Session Status on Mobile */}
              <div className="sm:hidden pb-3 border-b border-gray-200">
                <SessionStatus
                  isActive={isSessionActive}
                  isConnected={isLiveKitConnected}
                />
              </div>
              
              {/* AI Provider Toggles - Mobile */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">AI Providers</p>
                <div className="flex flex-col space-y-2">
                  <button
                    onClick={() => setShowOpenAI(!showOpenAI)}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg transition-all ${
                      isDarkMode ? 'border border-gray-700' : ''
                    } ${
                      showOpenAI
                        ? 'bg-green-100 text-green-700'
                        : isDarkMode
                          ? 'bg-gray-800 text-gray-300'
                          : 'bg-gray-100 text-gray-600'
                    }`}
                    title={showOpenAI ? "Hide OpenAI panel" : "Show OpenAI panel"}
                  >
                    <span className={`text-xs font-medium ${showOpenAI ? 'text-green-600' : 'text-gray-500'}`}>
                      OpenAI
                    </span>
                    <div className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      showOpenAI ? 'bg-green-600' : 'bg-gray-400'
                    }`}>
                      <span
                        className={`inline-block h-3 w-3 transform rounded-full bg-white shadow-md transition-transform ${
                          showOpenAI ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setShowGemini(!showGemini)}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg transition-all ${
                      isDarkMode ? 'border border-gray-700' : ''
                    } ${
                      showGemini
                        ? 'bg-blue-100 text-blue-700'
                        : isDarkMode
                          ? 'bg-gray-800 text-gray-300'
                          : 'bg-gray-100 text-gray-600'
                    }`}
                    title={showGemini ? "Hide Gemini panel" : "Show Gemini panel"}
                  >
                    <span className={`text-xs font-medium ${showGemini ? 'text-blue-600' : 'text-gray-500'}`}>
                      Gemini
                    </span>
                    <div className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      showGemini ? 'bg-blue-600' : 'bg-gray-400'
                    }`}>
                      <span
                        className={`inline-block h-3 w-3 transform rounded-full bg-white shadow-md transition-transform ${
                          showGemini ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </div>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 pb-20 sm:pb-8">
        



        {/* Shared Chat Input Bar - Full Glassy Effect */}
        <form onSubmit={handleSharedSend} className={`relative mb-6 backdrop-blur-xl rounded-2xl p-0.5 ${isDarkMode ? 'bg-gradient-to-r from-gray-800/60 to-gray-900/60' : 'bg-gradient-to-r from-white/20 to-white/10'}`}>
          <div className={`backdrop-blur-xl rounded-2xl border ${isDarkMode ? 'bg-gray-900/50 border-gray-700' : 'bg-white/10 border-white/30'}`}>
            <div className="relative flex items-center">
              <textarea
                ref={sharedChatInputRef}
                id="shared-chat-input"
                value={sharedChatInput}
                onChange={(e) => setSharedChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (!sharedChatInput.trim() || !isSessionActive || isSendingShared) return;
                    e.currentTarget.form?.requestSubmit();
                  }
                }}
                placeholder="Type your question or wait for transcription to auto-fill..."
                disabled={!isSessionActive || isSendingShared}
                rows={1}
                className={`
                  w-full px-5 py-3.5 pr-40 rounded-2xl transition-all bg-transparent resize-none overflow-hidden
                  ${!isSessionActive || isSendingShared
                    ? 'text-gray-400 cursor-not-allowed placeholder:text-gray-400'
                    : isDarkMode
                      ? 'text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500/50'
                      : 'text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-white/40'
                  }
                  text-sm font-medium
                `}
              />
              <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center space-x-1">
                {/* Paste Button */}
                <button
                  type="button"
                  onClick={handleSharedPaste}
                  disabled={!isSessionActive || isPastingShared || isSendingShared}
                  className={`
                    p-2.5 rounded-lg transition-all backdrop-blur-md
                    ${!isSessionActive || isPastingShared || isSendingShared
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-white/20 hover:text-gray-900'
                    }
                  `}
                  title="Paste from clipboard"
                >
                  {isPastingShared ? (
                    <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Clipboard className="w-4 h-4" />
                  )}
                </button>
                
                {/* Send Button */}
                <button
                  type="submit"
                  disabled={!sharedChatInput.trim() || !isSessionActive || isSendingShared}
                  className={`
                    flex items-center space-x-2 px-4 py-2.5 rounded-lg transition-all font-semibold text-sm
                    ${!sharedChatInput.trim() || !isSessionActive || isSendingShared
                      ? 'bg-white/10 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-400/60 to-blue-400/60 backdrop-blur-lg text-white hover:from-green-500/80 hover:to-blue-500/80 shadow-lg'
                    }
                  `}
                  title="Send to both OpenAI and Gemini"
                >
                  {isSendingShared ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Send</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* Mobile Swap Button - Only show when both panels are visible */}
        {showOpenAI && showGemini && (
          <div className="lg:hidden mb-4 flex justify-center">
            <button
              onClick={() => setPanelsSwapped(!panelsSwapped)}
              className={`flex items-center space-x-2 px-4 py-2.5 border-2 hover:border-primary-500 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 ${
                isDarkMode ? 'bg-gray-900 hover:bg-gray-800 border-gray-700' : 'bg-white hover:bg-gray-50 border-gray-300'
              }`}
            >
              <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                {panelsSwapped ? 'OpenAI First' : 'Gemini First'}
              </span>
              <ArrowLeftRight className={`w-4 h-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
            </button>
          </div>
        )}

        {/* Main Content - AI Responses */}
        <div className="relative">
          <div className={`grid gap-4 sm:gap-6 lg:gap-8 ${
            showOpenAI && showGemini ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'
          }`}>
            {/* First Panel (Gemini or OpenAI based on swap state) */}
            {!panelsSwapped ? (
              <>
                {/* Gemini Panel */}
                {showGemini && (
                  <div className="animate-fadeIn">
                    <AIProviderPanel
                      responses={aiResponses}
                      provider="gemini"
                      isSessionActive={isSessionActive}
                      isDarkMode={isDarkMode}
                    />
                  </div>
                )}
                
                {/* OpenAI Panel */}
                {showOpenAI && (
                  <div className="animate-fadeIn">
                    <AIProviderPanel
                      responses={aiResponses}
                      provider="openai"
                      isSessionActive={isSessionActive}
                      isDarkMode={isDarkMode}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                {/* OpenAI Panel */}
                {showOpenAI && (
                  <div className="animate-fadeIn">
                    <AIProviderPanel
                      responses={aiResponses}
                      provider="openai"
                      isSessionActive={isSessionActive}
                      isDarkMode={isDarkMode}
                    />
                  </div>
                )}
                
                {/* Gemini Panel */}
                {showGemini && (
                  <div className="animate-fadeIn">
                    <AIProviderPanel
                      responses={aiResponses}
                      provider="gemini"
                      isSessionActive={isSessionActive}
                      isDarkMode={isDarkMode}
                    />
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Swap Button - Only show when both panels are visible */}
          {showOpenAI && showGemini && (
            <div className="hidden lg:flex absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
              <button
                onClick={() => setPanelsSwapped(!panelsSwapped)}
                className={`group border-2 hover:border-primary-500 rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-200 ${
                  isDarkMode ? 'bg-gray-900 hover:bg-gray-800 border-gray-700' : 'bg-white hover:bg-gray-50 border-gray-300'
                }`}
                title="Swap panel positions"
              >
                <ArrowLeftRight className={`w-5 h-5 group-hover:text-primary-600 transition-colors ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
              </button>
            </div>
          )}
        </div>
        
        {/* No Panels Visible Message */}
        {!showOpenAI && !showGemini && (
          <div className="flex flex-col items-center justify-center h-96 text-gray-500">
            <Bot className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-lg font-medium">No AI Providers Selected</p>
            <p className="text-sm mt-2">Enable OpenAI or Gemini from the header to see responses</p>
          </div>
        )}

        {/* Left Sidebar - Desktop Only */}
        <div className={`hidden sm:flex sm:flex-col fixed top-0 bottom-0 left-0 w-16 shadow-2xl z-50 ${isDarkMode ? 'bg-black' : 'bg-gray-900'}`}>
          <div className="h-full w-full flex flex-col items-center py-4 gap-3">
            {/* Transcriptions Toggle */}
            <button
              onClick={() => setShowTranscriptions(!showTranscriptions)}
              className={`relative flex items-center justify-center w-11 h-11 rounded-xl transition-all duration-200 ${
                showTranscriptions 
                  ? 'bg-blue-600 text-white shadow-lg' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
              }`}
              title={showTranscriptions ? "Hide transcriptions" : "Show transcriptions"}
            >
              <MessageSquare className="w-5 h-5 shrink-0" strokeWidth={2} />
              {showTranscriptions && (
                <span className="absolute -right-0.5 -top-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-gray-900" />
              )}
            </button>

            {/* Question Bank Toggle */}
            <button
              onClick={() => setShowQuestionBank(!showQuestionBank)}
              className={`relative flex items-center justify-center w-11 h-11 rounded-xl transition-all duration-200 ${
                showQuestionBank 
                  ? 'bg-purple-600 text-white shadow-lg' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
              }`}
              title={showQuestionBank ? "Hide question bank" : "Show question bank"}
            >
              <BookOpen className="w-5 h-5 shrink-0" strokeWidth={2} />
              {showQuestionBank && (
                <span className="absolute -right-0.5 -top-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-gray-900" />
              )}
            </button>

            {/* Separator */}
            <div className="w-8 h-px bg-gray-600 shrink-0" />

            {/* Theme Toggle */}
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`relative flex items-center justify-center w-11 h-11 rounded-xl transition-all duration-200 ${
                isDarkMode
                  ? 'bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30'
                  : 'bg-gray-800 text-gray-200 hover:bg-gray-700'
              }`}
              title={isDarkMode ? 'Switch to bright mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? (
                <Sun className="w-5 h-5 shrink-0" strokeWidth={2} />
              ) : (
                <Moon className="w-5 h-5 shrink-0" strokeWidth={2} />
              )}
            </button>

            {/* Leave Session / Logout — pinned to bottom */}
            <button
              onClick={() => setShowLeaveSessionConfirm(true)}
              className="mt-auto relative flex items-center justify-center w-11 h-11 rounded-xl bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white transition-all duration-200"
              title="Leave Session"
            >
              <LogOut className="w-5 h-5 shrink-0" strokeWidth={2} />
            </button>
          </div>
        </div>
        
        {/* Bottom Navigation - Mobile Only */}
        <div className={`sm:hidden fixed bottom-0 left-0 right-0 shadow-2xl z-50 border-t ${isDarkMode ? 'bg-black border-gray-800' : 'bg-gray-900 border-gray-700'}`}>
          <div className="grid grid-cols-4 items-center gap-1 px-2 py-2">
            {/* Transcriptions Toggle */}
            <button
              onClick={() => setShowTranscriptions(!showTranscriptions)}
              className={`relative mx-auto flex items-center justify-center w-11 h-11 rounded-xl transition-all ${
                showTranscriptions 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <MessageSquare className="w-5 h-5 shrink-0" strokeWidth={2} />
              {showTranscriptions && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full" />
              )}
            </button>

            {/* Question Bank Toggle */}
            <button
              onClick={() => setShowQuestionBank(!showQuestionBank)}
              className={`relative mx-auto flex items-center justify-center w-11 h-11 rounded-xl transition-all ${
                showQuestionBank 
                  ? 'bg-purple-600 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <BookOpen className="w-5 h-5 shrink-0" strokeWidth={2} />
              {showQuestionBank && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full" />
              )}
            </button>

            {/* Theme Toggle */}
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`relative mx-auto flex items-center justify-center w-11 h-11 rounded-xl transition-all ${
                isDarkMode
                  ? 'bg-yellow-500/20 text-yellow-300'
                  : 'text-gray-400 hover:text-white'
              }`}
              title={isDarkMode ? 'Switch to bright mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? (
                <Sun className="w-5 h-5 shrink-0" strokeWidth={2} />
              ) : (
                <Moon className="w-5 h-5 shrink-0" strokeWidth={2} />
              )}
            </button>

            {/* Leave Session / Logout */}
            <button
              onClick={() => setShowLeaveSessionConfirm(true)}
              className="relative mx-auto flex items-center justify-center w-11 h-11 rounded-xl text-gray-400 hover:text-white transition-all"
              title="Leave Session"
            >
              <LogOut className="w-5 h-5 shrink-0" strokeWidth={2} />
            </button>
          </div>
        </div>

        {/* Transcriptions Panel */}
        <div 
          className={`fixed top-0 bottom-0 sm:left-16 left-0 transform ${
            showTranscriptions ? 'translate-x-0' : '-translate-x-full'
          } transition-transform duration-300 ease-in-out w-full sm:w-96 shadow-xl border-r z-40 ${
            isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
          }`}
        >
          <div className="h-full flex flex-col">
            {/* Mobile close button */}
            <div className={`sm:hidden flex justify-between items-center p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Transcriptions</h2>
              <button
                onClick={() => setShowTranscriptions(false)}
                className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-100 text-gray-700'}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Transcription Panel */}
            <div className="flex-1 overflow-hidden h-full">
              <TranscriptionPanel
                transcriptions={transcriptions}
                isActive={isSessionActive}
                onClear={clearTranscriptions}
              />
            </div>
          </div>
        </div>

        {/* Question Bank Panel */}
        <div 
          className={`fixed top-0 bottom-0 sm:left-16 left-0 transform ${
            showQuestionBank ? 'translate-x-0' : '-translate-x-full'
          } transition-transform duration-300 ease-in-out w-full sm:w-[32rem] lg:w-[36rem] shadow-xl border-r z-40 ${
            isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
          }`}
        >
          <div className="h-full flex flex-col">
            {/* Mobile close button */}
            <div className={`sm:hidden flex justify-between items-center p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Question Bank</h2>
              <button
                onClick={() => setShowQuestionBank(false)}
                className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-100 text-gray-700'}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Question Bank Panel */}
            <div className="flex-1 overflow-hidden h-full">
              <QuestionBank
                jobTitle={user.jobTitle}
                onClose={() => setShowQuestionBank(false)}
                isDarkMode={isDarkMode}
              />
            </div>
          </div>
        </div>

        {/* Credits exhausted — countdown then auto-stop */}
        {showHoursExhaustedModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div
              className={`rounded-xl shadow-2xl max-w-md w-full p-6 ${
                isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white'
              }`}
            >
              <h2 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                You are out of credits now
              </h2>
              {creditsExhaustedCountdown !== null && creditsExhaustedCountdown > 0 ? (
                <>
                  <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Your meeting hours for this billing period are finished. This session will stop
                    automatically.
                  </p>
                  <div
                    className={`flex items-center justify-center rounded-xl py-6 mb-2 ${
                      isDarkMode ? 'bg-gray-800' : 'bg-red-50'
                    }`}
                  >
                    <span
                      className={`text-5xl font-bold tabular-nums ${
                        isDarkMode ? 'text-red-400' : 'text-red-600'
                      }`}
                    >
                      {creditsExhaustedCountdown}
                    </span>
                  </div>
                  <p className={`text-xs text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Stopping session in {creditsExhaustedCountdown} second
                    {creditsExhaustedCountdown === 1 ? '' : 's'}…
                  </p>
                </>
              ) : (
                <>
                  <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Your interview session has been stopped. Upgrade your plan or wait for the next
                    billing period to continue.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setShowHoursExhaustedModal(false);
                      setCreditsExhaustedCountdown(null);
                    }}
                    className="w-full px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                  >
                    OK
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Leave Session Confirmation Dialog */}
        {showLeaveSessionConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`rounded-xl shadow-2xl max-w-md w-full p-4 sm:p-6 ${isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white'}`}>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <LogOut className="w-8 h-8 text-primary-600" />
                </div>
                <h2 className={`text-lg sm:text-xl font-semibold mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                  Leave Interview Session?
                </h2>
                <p className={`text-sm sm:text-base mb-4 sm:mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  You'll return to the mode selection screen and can start a new session anytime.
                </p>
                
                <div className="flex flex-col space-y-2 sm:space-y-3">
                  <button
                    onClick={async () => {
                      setShowLeaveSessionConfirm(false);
                      if (isSessionActive) {
                        await stopInterviewSession();
                      }
                      onLeaveSession();
                    }}
                    className="w-full px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Leave Session
                  </button>
                  <button
                    onClick={() => setShowLeaveSessionConfirm(false)}
                    className={`w-full px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base border rounded-lg transition-colors ${
                      isDarkMode
                        ? 'border-gray-600 text-gray-200 hover:bg-gray-800'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Interview Settings Panel - REMOVED */}
      </div>
    </div>
  );
};